import { ArrowRight, Download, History, Repeat } from "lucide-react";

// SupersededPlanView — a superseded plan was replaced by a newer one
// (e.g. via Revise & re-propose). Read-only record of the line items the
// plan held before it was replaced, plus a jump to the newer plan.

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}
function itemLine(it) {
  const proc = it.procedure_name || "Procedure";
  const tooth = it.tooth_number ? `#${it.tooth_number}` : "";
  const surface = it.surface ? ` ${it.surface}` : "";
  return [proc, tooth, surface].filter(Boolean).join(" ").trim();
}
function visitCountOf(it) {
  const v = Array.isArray(it?.procedure_visits) ? it.procedure_visits.length : 0;
  if (v > 1) return v;
  return Number(it?.visit_count) > 1 ? Number(it.visit_count) : 1;
}

export default function SupersededPlanView({ plan, onOpenSuccessor, onDownloadPdf }) {
  const items = plan?.items ?? [];
  const total = items.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);
  const successorId = plan?.superseded_by_id || null;

  return (
    <div data-testid="superseded-plan-view" className="space-y-3">
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600 inline-flex items-center gap-1.5">
          <History className="w-4 h-4 text-slate-400" /> This proposal was replaced by a newer plan.
        </p>
        {successorId ? (
          <button
            type="button"
            onClick={() => onOpenSuccessor?.(successorId)}
            data-testid="superseded-open-successor"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:border-slate-400 shrink-0"
          >
            Open the newer plan <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Original scope (before it was replaced)</p>
      <ul className="space-y-2">
        {items.map((it) => {
          const vc = visitCountOf(it);
          return (
            <li
              key={it.id}
              data-testid={`superseded-item-${it.id}`}
              className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2.5"
            >
              <span className="text-sm text-slate-700">
                {itemLine(it)}
                {vc > 1 ? (
                  <span className="ml-1.5 inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded align-middle">
                    <Repeat className="w-2.5 h-2.5" /> {vc} visits
                  </span>
                ) : null}
              </span>
              <span className="text-xs text-slate-500 tabular-nums">{formatPHP(Number(it.fee_centavos) || 0)}</span>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <span className="text-sm text-slate-600">
          Original total <span className="text-slate-900 font-medium tabular-nums">{formatPHP(total)}</span>
        </span>
        <button
          type="button"
          onClick={() => onDownloadPdf?.(plan.id)}
          data-testid="superseded-pdf"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm hover:border-slate-400"
        >
          <Download className="w-3.5 h-3.5" /> PDF
        </button>
      </div>
    </div>
  );
}
